import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GUEST_CART_STORAGE_KEY } from '../constants/storageKeys';
import { createUuid } from '../utils/uuid';

function now() {
  return Date.now();
}

function safeString(v) {
  const s = String(v ?? '').trim();
  return s ? s : '';
}

function sameLineSignature(a, b) {
  if (!a || !b) return false;
  const itemIdA = safeString(a.itemId);
  const itemIdB = safeString(b.itemId);
  if (!itemIdA || itemIdA !== itemIdB) return false;
  const varA = safeString(a?.variant?.id);
  const varB = safeString(b?.variant?.id);
  if (varA !== varB) return false;
  const addonsA = Array.isArray(a?.addons)
    ? a.addons
        .map((x) => {
          const id = safeString(x?.id);
          if (!id) return '';
          const q = Math.max(1, Number(x?.qty ?? 1));
          return `${id}:${q}`;
        })
        .filter(Boolean)
        .sort()
        .join(',')
    : '';
  const addonsB = Array.isArray(b?.addons)
    ? b.addons
        .map((x) => {
          const id = safeString(x?.id);
          if (!id) return '';
          const q = Math.max(1, Number(x?.qty ?? 1));
          return `${id}:${q}`;
        })
        .filter(Boolean)
        .sort()
        .join(',')
    : '';
  return addonsA === addonsB;
}

export const useGuestCartStore = create(
  persist(
    (set, get) => ({
      guestId: createUuid(),
      restaurant: null, // { id, name }
      lines: [], // [{ lineId, itemId, name, basePrice, qty, notes, variant, addons, imageUrl, imageColor, imageInitial }]
      coupon: null, // { code, type, value }
      fee: {
        delivery: 0,
        taxRatePct: 0,
      },
      meta: {
        lastUpdatedAt: now(),
      },
      ui: {
        isDrawerOpen: false,
        pendingReplaceConfirm: null, // { nextRestaurant, nextLineInput }
      },
      inFlight: {
        addSig: {}, // signature -> timestamp (prevents rapid double-click)
        lineOps: {}, // lineId -> boolean
      },

      actions: {
        openDrawer: () => set((s) => ({ ui: { ...s.ui, isDrawerOpen: true } })),
        closeDrawer: () => set((s) => ({ ui: { ...s.ui, isDrawerOpen: false } })),
        toggleDrawer: (open) =>
          set((s) => ({ ui: { ...s.ui, isDrawerOpen: typeof open === 'boolean' ? open : !s.ui.isDrawerOpen } })),

        setRestaurant: (restaurant) =>
          set((s) => ({
            restaurant: restaurant && safeString(restaurant.id) ? { id: safeString(restaurant.id), name: safeString(restaurant.name) } : null,
            meta: { ...s.meta, lastUpdatedAt: now() },
          })),

        clearCart: () =>
          set((s) => ({
            restaurant: null,
            lines: [],
            coupon: null,
            meta: { ...s.meta, lastUpdatedAt: now() },
          })),

        setLineNotes: (lineId, notes) =>
          set((s) => ({
            lines: s.lines.map((l) => (l.lineId === lineId ? { ...l, notes: String(notes ?? '') } : l)),
            meta: { ...s.meta, lastUpdatedAt: now() },
          })),

        removeLine: (lineId) =>
          set((s) => ({
            lines: s.lines.filter((l) => l.lineId !== lineId),
            restaurant: s.lines.filter((l) => l.lineId !== lineId).length ? s.restaurant : null,
            meta: { ...s.meta, lastUpdatedAt: now() },
          })),

        setQty: (lineId, qty) =>
          set((s) => {
            const q = Math.max(1, Number(qty || 1));
            return {
              lines: s.lines.map((l) => (l.lineId === lineId ? { ...l, qty: q } : l)),
              meta: { ...s.meta, lastUpdatedAt: now() },
            };
          }),

        incQty: (lineId) => {
          const line = get().lines.find((l) => l.lineId === lineId);
          if (!line) return;
          get().actions.setQty(lineId, (line.qty || 1) + 1);
        },

        decQty: (lineId) => {
          const line = get().lines.find((l) => l.lineId === lineId);
          if (!line) return;
          const next = (line.qty || 1) - 1;
          if (next <= 0) {
            get().actions.removeLine(lineId);
          } else {
            get().actions.setQty(lineId, next);
          }
        },

        /**
         * Adds a line or increments an existing line with same item/variant/addons signature.
         * Notes are not part of signature (so user can add notes later without creating duplicates).
         */
        addOrIncLine: ({ restaurant, lineInput, preventRapidMs = 450 }) => {
          const rid = safeString(restaurant?.id);
          if (!rid) throw new Error('Missing restaurant id');
          const itemId = safeString(lineInput?.itemId);
          if (!itemId) throw new Error('Missing item id');

          const signature = JSON.stringify({
            rid,
            itemId,
            variantId: safeString(lineInput?.variant?.id),
            addonIds: Array.isArray(lineInput?.addons)
              ? lineInput.addons
                  .map((a) => {
                    const id = safeString(a?.id);
                    if (!id) return '';
                    const q = Math.max(1, Number(a?.qty ?? 1));
                    return `${id}:${q}`;
                  })
                  .filter(Boolean)
                  .sort()
              : [],
          });

          const last = get().inFlight.addSig?.[signature] || 0;
          const t = now();
          if (preventRapidMs && t - last < preventRapidMs) {
            return { type: 'ignored', reason: 'rapid' };
          }

          set((s) => ({
            inFlight: { ...s.inFlight, addSig: { ...(s.inFlight.addSig || {}), [signature]: t } },
          }));

          const currentRestaurant = get().restaurant;
          const differentRestaurant = currentRestaurant && safeString(currentRestaurant.id) && safeString(currentRestaurant.id) !== rid;
          if (differentRestaurant && get().lines.length) {
            set((s) => ({
              ui: {
                ...s.ui,
                pendingReplaceConfirm: {
                  nextRestaurant: { id: rid, name: safeString(restaurant?.name) },
                  nextLineInput: { ...lineInput },
                },
              },
            }));
            return { type: 'needs_confirm_replace' };
          }

          set((s) => {
            const existing = s.lines.find((l) => sameLineSignature(l, lineInput));
            const nextLines = existing
              ? s.lines.map((l) => (l.lineId === existing.lineId ? { ...l, qty: Math.max(1, (l.qty || 1) + (lineInput.qty || 1)) } : l))
              : [
                  ...s.lines,
                  {
                    lineId: createUuid(),
                    itemId,
                    name: safeString(lineInput?.name),
                    basePrice: lineInput?.basePrice ?? 0,
                    qty: Math.max(1, Number(lineInput?.qty || 1)),
                    notes: safeString(lineInput?.notes),
                    variant: lineInput?.variant || null, // { id, name, priceDelta }
                    addons: Array.isArray(lineInput?.addons) ? lineInput.addons : [],
                    imageUrl: safeString(lineInput?.imageUrl),
                    imageColor: safeString(lineInput?.imageColor),
                    imageInitial: safeString(lineInput?.imageInitial),
                  },
                ];

            return {
              restaurant: s.restaurant || { id: rid, name: safeString(restaurant?.name) },
              lines: nextLines,
              meta: { ...s.meta, lastUpdatedAt: now() },
            };
          });

          return { type: 'added' };
        },

        confirmReplaceAndAdd: () => {
          const pending = get().ui.pendingReplaceConfirm;
          if (!pending) return { type: 'noop' };
          const { nextRestaurant, nextLineInput } = pending;
          set((s) => ({
            restaurant: nextRestaurant,
            lines: [],
            coupon: null,
            ui: { ...s.ui, pendingReplaceConfirm: null, isDrawerOpen: true },
            meta: { ...s.meta, lastUpdatedAt: now() },
          }));
          const res = get().actions.addOrIncLine({ restaurant: nextRestaurant, lineInput: nextLineInput, preventRapidMs: 0 });
          return res;
        },

        cancelReplace: () => set((s) => ({ ui: { ...s.ui, pendingReplaceConfirm: null } })),

        setFee: (partial) =>
          set((s) => ({
            fee: { ...s.fee, ...(partial && typeof partial === 'object' ? partial : {}) },
            meta: { ...s.meta, lastUpdatedAt: now() },
          })),
      },
    }),
    {
      name: GUEST_CART_STORAGE_KEY,
      version: 2,
      migrate: (persistedState, fromVersion) => {
        if (fromVersion < 2 && persistedState && typeof persistedState === 'object') {
          const st = { ...persistedState };
          delete st.cartToken;
          if (st.meta && typeof st.meta === 'object') {
            const { lastServerSyncAt, ...mrest } = st.meta;
            st.meta = mrest;
          }
          return st;
        }
        return persistedState;
      },
      partialize: (s) => ({
        guestId: s.guestId,
        restaurant: s.restaurant,
        lines: s.lines,
        coupon: s.coupon,
        fee: s.fee,
        meta: s.meta,
      }),
    }
  )
);

